/**
 * RP89 → RP92 (D11 B, ADM-A1, ADM-R5) — Assistant « Premier jour » (administrateur).
 *
 * 5 étapes que le gérant suit seul ; les envois d'e-mails restent en pause jusqu'à
 * l'étape 4 (pause activée à l'ouverture de l'assistant, levée sur confirmation) :
 *   1. Identité du cabinet (nom, logo, couleurs, mentions de l'Ordre) ;
 *   2. Inviter l'équipe (e-mail, Collaborateur / Associé, accès restreint) : invitation
 *      par lien, envoyée à l'étape 4 ;
 *   3. Premières entreprises (formulaire ou import CSV) ;
 *   4. Reprendre les envois → « Tout est prêt : reprendre les envois… » (B6, P1#27 : le
 *      titre dit ce que fait l'étape) ;
 *   5. B6 (P1#27) — Votre premier chantier : créer, importer un chantier en cours, ou le
 *      projet exemple (chantier fictif complet, cohérent avec son étape).
 * B6 — l'assistant s'ouvre tout seul à la première connexion de l'administrateur d'un
 * cabinet sans chantier réel (CartePremierJour), il n'est plus caché dans Paramètres.
 *
 * Parcours cible (07-admin-cabinet §5.1, 2 collaborateurs + 1 entreprise) : ≈12 clics.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Buildings, UsersThree, Wrench, PaperPlaneTilt, CheckCircle, Plus, Trash, UploadSimple, ArrowRight,
  HouseLine, FileArrowUp, Sparkle,
} from '@phosphor-icons/react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api/admin/premier-jour`;
const opts = { withCredentials: true };

const ETAPES = [
  { id: 'cabinet', titre: 'Identité du cabinet', icon: Buildings },
  { id: 'equipe', titre: "Inviter l'équipe", icon: UsersThree },
  { id: 'entreprises', titre: 'Premières entreprises', icon: Wrench },
  { id: 'envois', titre: 'Reprendre les envois', icon: PaperPlaneTilt },
  { id: 'premier_chantier', titre: 'Votre premier chantier', icon: HouseLine },
];
const ETAPE_PREMIER_CHANTIER = 4;

const erreurDe = (e, defaut) => e?.response?.data?.detail || defaut;

export default function PremierJour() {
  const { user, isAdmin } = useAuth();
  const [etat, setEtat] = useState(null);
  const [etape, setEtape] = useState(0);

  const recharger = async () => {
    const { data } = await axios.get(API, opts);
    setEtat(data);
    return data;
  };

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        let data = await recharger();
        // B6 — configuration déjà terminée : on arrive sur « Votre premier chantier »
        if (data.statut === 'termine') setEtape(ETAPE_PREMIER_CHANTIER);
        // D11 — envois d'e-mails en pause d'office tant que l'assistant n'est pas fini
        if (data.statut !== 'en_cours' && data.statut !== 'termine') {
          data = (await axios.post(`${API}/commencer`, null, opts)).data;
          setEtat(data);
        }
      } catch (e) {
        toast.error(erreurDe(e, "Impossible d'ouvrir l'assistant"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;

  const suivant = async () => {
    await recharger();
    setEtape((n) => Math.min(n + 1, ETAPES.length - 1));
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-4xl" data-testid="premier-jour-page">
        <div className="mb-6">
          <h1 className="text-3xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Premier jour : configurer le cabinet
          </h1>
          <p className="text-[#6B6B6B] mt-1">
            Cinq étapes, dans l'ordre. Rien ne part aux entreprises ni à l'équipe avant l'étape 4, « Reprendre les envois ».
          </p>
          {etat?.pause_envois && (
            <p className="inline-block mt-3 text-xs font-medium px-2 py-1 bg-[#FFF4E5] text-[#8A5A00] border border-[#F3D9A4]" data-testid="premier-jour-pause">
              Envois d'e-mails en pause
            </p>
          )}
        </div>

        <ol className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="premier-jour-etapes">
          {ETAPES.map((e, i) => {
            const Icon = e.icon;
            const fait = etat?.etapes?.[e.id];
            const actif = i === etape;
            return (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => setEtape(i)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm border text-left ${actif ? 'border-[#2C2C2C] bg-[#2C2C2C] text-white' : 'border-[#E0E0DB] bg-white text-[#2C2C2C]'}`}
                  data-testid={`premier-jour-etape-${e.id}`}
                >
                  {fait ? <CheckCircle size={16} weight="fill" className={actif ? 'text-white' : 'text-[#2D6A4F]'} /> : <Icon size={16} />}
                  <span>{i + 1}. {e.titre}</span>
                </button>
              </li>
            );
          })}
        </ol>

        {!etat ? (
          <p className="text-sm text-[#6B6B6B]">Chargement…</p>
        ) : (
          <div className="border border-[#E0E0DB] bg-white p-5">
            {etape === 0 && <EtapeCabinet etat={etat} onSuivant={suivant} />}
            {etape === 1 && <EtapeEquipe onSuivant={suivant} />}
            {etape === 2 && <EtapeEntreprises onSuivant={suivant} onRecharger={recharger} />}
            {etape === 3 && <EtapeEnvois etat={etat} onTermine={setEtat} onSuivant={() => setEtape(ETAPE_PREMIER_CHANTIER)} />}
            {etape === 4 && <EtapePremierChantier etat={etat} onRecharger={recharger} />}
          </div>
        )}
      </div>
    </Layout>
  );
}

// ==================== Étape 1 — identité du cabinet ====================

function Champ({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="block text-[#6B6B6B] mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls = 'w-full border border-[#E0E0DB] px-3 py-2 text-sm focus:outline-none focus:border-[#2C2C2C]';

function EtapeCabinet({ etat, onSuivant }) {
  const [form, setForm] = useState(() => ({
    nom: '', adresse: '', ordre_architectes: '', siret: '', rcs: '', email_contact: '', telephone: '',
    couleur_primaire: '#C4773B', couleur_secondaire: '#1E3A5F',
    ...Object.fromEntries(Object.entries(etat.cabinet || {}).filter(([, v]) => v)),
  }));
  const [logoApercu, setLogoApercu] = useState(etat.cabinet?.logo_url ? `${API_URL}/api/cabinet/logo` : null);
  const [envoi, setEnvoi] = useState(false);
  const fichier = useRef(null);
  const maj = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const envoyerLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/cabinet/logo`, fd, opts);
      setForm((f) => ({
        ...f,
        couleur_primaire: data.couleur_primaire || f.couleur_primaire,
        couleur_secondaire: data.couleur_secondaire || f.couleur_secondaire,
      }));
      setLogoApercu(URL.createObjectURL(file));
      toast.success(data.couleur_primaire ? 'Logo enregistré, couleurs reprises du logo' : 'Logo enregistré');
    } catch (err) {
      toast.error(erreurDe(err, "Le logo n'a pas été enregistré"));
    } finally {
      e.target.value = '';
    }
  };

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const { logo_url: _logo, ...champs } = form;
      await axios.put(`${API}/cabinet`, champs, opts);
      toast.success('Identité du cabinet enregistrée');
      onSuivant();
    } catch (err) {
      toast.error(erreurDe(err, "L'identité du cabinet n'a pas été enregistrée"));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="premier-jour-cabinet">
      <h2 className="text-lg font-semibold text-[#2C2C2C]">1. Identité du cabinet</h2>
      <p className="text-sm text-[#6B6B6B]">Vos comptes rendus, e-mails et documents sortiront à ce nom, avec ce logo.</p>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 border border-dashed border-[#E0E0DB] flex items-center justify-center bg-[#FAFAF8]">
          {logoApercu ? <img src={logoApercu} alt="Logo du cabinet" className="max-w-full max-h-full object-contain" /> : <Buildings size={28} className="text-[#9A9A9A]" />}
        </div>
        <div>
          <input ref={fichier} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={envoyerLogo} data-testid="premier-jour-logo-input" />
          <Button type="button" variant="outline" className="rounded-none gap-2" onClick={() => fichier.current?.click()} data-testid="premier-jour-logo">
            <UploadSimple size={16} /> Choisir un logo
          </Button>
          <p className="text-xs text-[#9A9A9A] mt-1">PNG, JPG, SVG ou WEBP, 2 Mo maximum. Les couleurs sont reprises du logo.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Champ label="Nom du cabinet *"><input className={inputCls} value={form.nom} onChange={maj('nom')} data-testid="premier-jour-nom" /></Champ>
        <Champ label="Adresse"><input className={inputCls} value={form.adresse} onChange={maj('adresse')} /></Champ>
        <Champ label="Mention de l'Ordre des architectes"><input className={inputCls} value={form.ordre_architectes} onChange={maj('ordre_architectes')} placeholder="Inscrit à l'Ordre des architectes de … sous le n° …" /></Champ>
        <Champ label="SIRET"><input className={inputCls} value={form.siret} onChange={maj('siret')} /></Champ>
        <Champ label="E-mail de contact"><input className={inputCls} type="email" value={form.email_contact} onChange={maj('email_contact')} /></Champ>
        <Champ label="Téléphone"><input className={inputCls} value={form.telephone} onChange={maj('telephone')} /></Champ>
        <Champ label="Couleur principale"><input type="color" value={form.couleur_primaire} onChange={maj('couleur_primaire')} className="h-9 w-20 border border-[#E0E0DB]" /></Champ>
        <Champ label="Couleur secondaire"><input type="color" value={form.couleur_secondaire} onChange={maj('couleur_secondaire')} className="h-9 w-20 border border-[#E0E0DB]" /></Champ>
      </div>
      <div className="flex justify-end">
        <Button onClick={enregistrer} disabled={envoi || !form.nom?.trim()} className="rounded-none bg-[#2C2C2C] gap-2" data-testid="premier-jour-cabinet-suivant">
          Enregistrer et continuer <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

// ==================== Étape 2 — équipe ====================

const ligneVide = () => ({ nom: '', email: '', role: 'collaborateur', acces_restreint: false });

function EtapeEquipe({ onSuivant }) {
  const [lignes, setLignes] = useState([ligneVide()]);
  const [resultat, setResultat] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const maj = (i, k, v) => setLignes((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const inviter = async () => {
    const membres = lignes.filter((l) => l.email.trim());
    if (!membres.length) { onSuivant(); return; }
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/equipe`, { membres }, opts);
      setResultat(data);
      if (data.erreurs.length === 0) {
        toast.success(`${data.crees.length} invitation(s) préparée(s)`);
        onSuivant();
      } else {
        // On ne garde que les lignes en erreur, à corriger
        const enErreur = new Set(data.erreurs.map((e) => e.email));
        setLignes(membres.filter((l) => enErreur.has(l.email.trim().toLowerCase())));
      }
    } catch (err) {
      toast.error(erreurDe(err, "Les invitations n'ont pas été préparées"));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="premier-jour-equipe">
      <h2 className="text-lg font-semibold text-[#2C2C2C]">2. Inviter l'équipe</h2>
      <p className="text-sm text-[#6B6B6B]">
        Chaque personne recevra, à la fin de l'assistant, un lien personnel pour activer son compte.
        Un Associé voit tous les projets ; un Collaborateur aussi, sauf s'il a un accès restreint.
      </p>
      <div className="space-y-2">
        {lignes.map((l, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`premier-jour-membre-${i}`}>
            <input className={`${inputCls} md:col-span-3`} placeholder="Nom" value={l.nom} onChange={(e) => maj(i, 'nom', e.target.value)} />
            <input className={`${inputCls} md:col-span-4`} placeholder="E-mail" type="email" value={l.email} onChange={(e) => maj(i, 'email', e.target.value)} />
            <select className={`${inputCls} md:col-span-2`} value={l.role} onChange={(e) => maj(i, 'role', e.target.value)} aria-label="Rôle">
              <option value="collaborateur">Collaborateur</option>
              <option value="associe">Associé</option>
            </select>
            <label className="md:col-span-2 flex items-center gap-2 text-xs text-[#6B6B6B]" title="Ne voit que les projets où il est ajouté">
              <input type="checkbox" checked={l.acces_restreint} onChange={(e) => maj(i, 'acces_restreint', e.target.checked)} />
              Accès restreint
            </label>
            <button type="button" className="md:col-span-1 text-[#9A9A9A] hover:text-[#C75B39] justify-self-start" onClick={() => setLignes((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : [ligneVide()]))} aria-label="Retirer la ligne">
              <Trash size={16} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="text-sm text-[#1E3A5F] flex items-center gap-1" onClick={() => setLignes((ls) => [...ls, ligneVide()])} data-testid="premier-jour-ajouter-membre">
        <Plus size={14} /> Ajouter une ligne
      </button>
      {resultat?.erreurs?.length > 0 && (
        <ul className="text-sm text-[#C75B39] space-y-1" data-testid="premier-jour-equipe-erreurs">
          {resultat.erreurs.map((e, i) => <li key={i}>{e.email || `Ligne ${e.ligne}`} : {e.message}</li>)}
        </ul>
      )}
      <div className="flex justify-between">
        <button type="button" className="text-sm text-[#6B6B6B] underline" onClick={onSuivant}>Passer cette étape</button>
        <Button onClick={inviter} disabled={envoi} className="rounded-none bg-[#2C2C2C] gap-2" data-testid="premier-jour-equipe-suivant">
          Inviter et continuer <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

// ==================== Étape 3 — entreprises + projet exemple ====================

const entrepriseVide = () => ({ nom: '', email: '', corps_etat: '' });

function EtapeEntreprises({ onSuivant, onRecharger }) {
  const [lignes, setLignes] = useState([entrepriseVide()]);
  const [erreurs, setErreurs] = useState([]);
  const [csv, setCsv] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const fichier = useRef(null);
  const maj = (i, k, v) => setLignes((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const ajouter = async () => {
    const entreprises = lignes.filter((l) => l.nom.trim() || l.email.trim());
    if (!entreprises.length) { onSuivant(); return; }
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/entreprises`, { entreprises }, opts);
      setErreurs(data.erreurs);
      if (data.creees.length) toast.success(`${data.creees.length} entreprise(s) ajoutée(s) à l'annuaire`);
      if (data.erreurs.length === 0) onSuivant();
      else setLignes(entreprises.filter((_, i) => data.erreurs.some((e) => e.ligne === i + 1)));
    } catch (err) {
      toast.error(erreurDe(err, "Les entreprises n'ont pas été ajoutées"));
    } finally {
      setEnvoi(false);
    }
  };

  const lireCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const texte = String(reader.result || '');
      setCsv(texte);
      try {
        const { data } = await axios.post(`${API}/entreprises/csv`, { csv: texte, apercu: true }, opts);
        setApercu(data);
      } catch (err) {
        toast.error(erreurDe(err, 'Fichier illisible'));
      }
    };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  const importer = async () => {
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/entreprises/csv`, { csv, apercu: false }, opts);
      toast.success(`${data.creees.length} entreprise(s) importée(s)`);
      setApercu({ ...data, valides: [] });
      await onRecharger();
    } catch (err) {
      toast.error(erreurDe(err, "L'import n'a pas abouti"));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="premier-jour-entreprises">
      <h2 className="text-lg font-semibold text-[#2C2C2C]">3. Premières entreprises</h2>
      <p className="text-sm text-[#6B6B6B]">Les entreprises avec une adresse e-mail reçoivent une invitation à la fin de l'assistant.</p>
      <div className="space-y-2">
        {lignes.map((l, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`premier-jour-entreprise-${i}`}>
            <input className={`${inputCls} md:col-span-4`} placeholder="Nom de l'entreprise" value={l.nom} onChange={(e) => maj(i, 'nom', e.target.value)} />
            <input className={`${inputCls} md:col-span-4`} placeholder="E-mail" type="email" value={l.email} onChange={(e) => maj(i, 'email', e.target.value)} />
            <input className={`${inputCls} md:col-span-3`} placeholder="Corps d'état" value={l.corps_etat} onChange={(e) => maj(i, 'corps_etat', e.target.value)} />
            <button type="button" className="md:col-span-1 text-[#9A9A9A] hover:text-[#C75B39] justify-self-start" onClick={() => setLignes((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : [entrepriseVide()]))} aria-label="Retirer la ligne">
              <Trash size={16} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="text-sm text-[#1E3A5F] flex items-center gap-1" onClick={() => setLignes((ls) => [...ls, entrepriseVide()])}>
        <Plus size={14} /> Ajouter une ligne
      </button>
      {erreurs.length > 0 && (
        <ul className="text-sm text-[#C75B39] space-y-1">{erreurs.map((e, i) => <li key={i}>Ligne {e.ligne} : {e.message}</li>)}</ul>
      )}

      <div className="border-t border-[#E0E0DB] pt-4">
        <p className="text-sm font-medium text-[#2C2C2C]">Ou importer un fichier CSV</p>
        <p className="text-xs text-[#9A9A9A] mb-2">Colonnes : nom ; e-mail ; corps d'état (séparateur « ; » ou « , », ligne d'en-tête facultative).</p>
        <input ref={fichier} type="file" accept=".csv,text/csv" className="hidden" onChange={lireCsv} data-testid="premier-jour-csv-input" />
        <Button type="button" variant="outline" className="rounded-none gap-2" onClick={() => fichier.current?.click()}>
          <UploadSimple size={16} /> Choisir un fichier CSV
        </Button>
        {apercu && (
          <div className="mt-3 text-sm" data-testid="premier-jour-csv-apercu">
            {apercu.apercu && <p>{apercu.valides.length} entreprise(s) prête(s) à importer.</p>}
            {apercu.erreurs.length > 0 && (
              <ul className="text-[#C75B39] mt-1 space-y-1">{apercu.erreurs.map((e, i) => <li key={i}>Ligne {e.ligne} : {e.message}</li>)}</ul>
            )}
            {apercu.apercu && apercu.valides.length > 0 && (
              <Button onClick={importer} disabled={envoi} className="rounded-none bg-[#1E3A5F] mt-2">
                Importer {apercu.valides.length} entreprise(s)
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button type="button" className="text-sm text-[#6B6B6B] underline" onClick={onSuivant}>Passer cette étape</button>
        <Button onClick={ajouter} disabled={envoi} className="rounded-none bg-[#2C2C2C] gap-2" data-testid="premier-jour-entreprises-suivant">
          Ajouter et continuer <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

// ==================== Étape 4 — reprise des envois ====================

function EtapeEnvois({ etat, onTermine, onSuivant }) {
  const [confirme, setConfirme] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState(null);
  const invites = etat.invitations_a_envoyer || [];

  const terminer = async () => {
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/terminer`, { confirmer: true }, opts);
      setResultat(data);
      onTermine(data.etat);
      toast.success(`Configuration terminée — ${data.invitations_envoyees} invitation(s) envoyée(s)`);
    } catch (err) {
      toast.error(erreurDe(err, "La configuration n'a pas pu être terminée"));
    } finally {
      setEnvoi(false);
    }
  };

  if (resultat || etat.statut === 'termine') {
    return (
      <div className="space-y-3" data-testid="premier-jour-termine">
        <h2 className="text-lg font-semibold text-[#2C2C2C] flex items-center gap-2"><CheckCircle size={20} weight="fill" className="text-[#2D6A4F]" /> Configuration terminée</h2>
        {resultat ? <p className="text-sm text-[#6B6B6B]">{resultat.invitations_envoyees} invitation(s) envoyée(s). Les envois d'e-mails ont repris.</p>
          : <p className="text-sm text-[#6B6B6B]">Les envois d'e-mails ont repris.</p>}
        {resultat?.erreurs?.length > 0 && (
          <div className="text-sm">
            <p className="text-[#C75B39]">Certaines invitations ne sont pas parties : transmettez ce lien à la personne.</p>
            <ul className="mt-1 space-y-1">{resultat.erreurs.map((e, i) => <li key={i} className="break-all">{e.email} : {e.lien || e.message}</li>)}</ul>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 pt-2">
          <Button onClick={onSuivant} className="rounded-none bg-[#2C2C2C] gap-2" data-testid="premier-jour-vers-premier-chantier">
            Dernière étape : votre premier chantier <ArrowRight size={16} />
          </Button>
          <Link to="/dashboard" className="inline-block text-sm underline text-[#1E3A5F]">Aller au tableau de bord</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="premier-jour-envois">
      <h2 className="text-lg font-semibold text-[#2C2C2C]">4. Reprendre les envois</h2>
      <p className="text-sm text-[#6B6B6B]">
        {etat.pause_envois
          ? "Aucun e-mail n'est parti pendant la configuration. Quand tout est prêt, reprenez les envois : les invitations ci-dessous partiront, et les notifications de test bloquées seront supprimées sans être envoyées."
          : 'Les envois ne sont pas en pause. Les invitations ci-dessous partiront maintenant.'}
      </p>
      <div className="text-sm">
        <p className="font-medium text-[#2C2C2C]">Invitations à envoyer ({invites.length})</p>
        {invites.length === 0 ? (
          <p className="text-[#9A9A9A]">Aucune invitation en attente.</p>
        ) : (
          <ul className="mt-1 space-y-1">{invites.map((u) => <li key={u.id}>{u.nom} — {u.email}</li>)}</ul>
        )}
        {etat.emails_en_attente > 0 && (
          <p className="text-xs text-[#9A9A9A] mt-2">{etat.emails_en_attente} notification(s) de test bloquée(s) seront supprimées.</p>
        )}
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} data-testid="premier-jour-confirmer" />
        <span>Je confirme : les e-mails partiront désormais normalement (équipe, entreprises, clients).</span>
      </label>
      <div className="flex justify-end">
        <Button onClick={terminer} disabled={!confirme || envoi} className="rounded-none bg-[#2D6A4F] gap-2" data-testid="premier-jour-terminer">
          <PaperPlaneTilt size={16} /> Tout est prêt : reprendre les envois et envoyer les invitations
        </Button>
      </div>
    </div>
  );
}

// ==================== Étape 5 — votre premier chantier (B6, P1#27) ====================

function EtapePremierChantier({ etat, onRecharger }) {
  const [envoi, setEnvoi] = useState(false);

  const projetExemple = async () => {
    setEnvoi(true);
    try {
      await axios.post(`${API}/projet-exemple`, null, opts);
      toast.success('Projet exemple créé (visible dans Chantiers)');
      await onRecharger();
    } catch (err) {
      toast.error(erreurDe(err, "Le projet exemple n'a pas été créé"));
    } finally {
      setEnvoi(false);
    }
  };

  const carte = 'border border-[#E0E0DB] p-4 flex flex-col gap-2 bg-white';
  return (
    <div className="space-y-4" data-testid="premier-jour-premier-chantier">
      <h2 className="text-lg font-semibold text-[#2C2C2C]">5. Votre premier chantier</h2>
      <p className="text-sm text-[#6B6B6B]">
        {etat.chantier_reel
          ? 'Le cabinet a déjà un chantier : vous pouvez en ajouter un autre, ou découvrir le projet exemple.'
          : "Trois façons de commencer. Le projet exemple est un chantier fictif en travaux, complet, pour voir l'outil en situation."}
      </p>
      <div className="grid md:grid-cols-3 gap-3">
        <div className={carte}>
          <HouseLine size={22} className="text-[#C75B39]" />
          <p className="font-medium text-[#2C2C2C]">Créer un chantier</p>
          <p className="text-xs text-[#6B6B6B] flex-1">Nom, adresse, client et étape : le reste se complète au fil du projet.</p>
          <Link to="/chantiers/new" className="inline-flex items-center gap-2 px-3 py-2 bg-[#2C2C2C] text-white text-sm hover:bg-[#4A4A4A] self-start" data-testid="premier-jour-creer-chantier">
            Créer un chantier <ArrowRight size={14} />
          </Link>
        </div>
        <div className={carte}>
          <FileArrowUp size={22} className="text-[#1E3A5F]" />
          <p className="font-medium text-[#2C2C2C]">Importer un chantier en cours</p>
          <p className="text-xs text-[#6B6B6B] flex-1">Reprenez un chantier déjà commencé à partir de vos documents (DPGF, planning, comptes rendus).</p>
          <Link to="/chantiers/import" className="inline-flex items-center gap-2 px-3 py-2 border border-[#1E3A5F] text-[#1E3A5F] text-sm hover:bg-[#F5F8FC] self-start" data-testid="premier-jour-importer-chantier">
            Importer <ArrowRight size={14} />
          </Link>
        </div>
        <div className={carte}>
          <Sparkle size={22} className="text-[#2D6A4F]" />
          <p className="font-medium text-[#2C2C2C]">Projet exemple</p>
          <p className="text-xs text-[#6B6B6B] flex-1">
            Un chantier fictif en travaux : équipe, marchés signés, comptes rendus diffusés, actions, plan annoté, situation et avenant. Vous pourrez le supprimer.
          </p>
          {etat.etapes?.projet_exemple ? (
            <p className="text-sm text-[#2D6A4F] flex items-center gap-1"><CheckCircle size={16} weight="fill" /> Projet exemple créé{etat.projet_exemple_id && <> — <Link className="underline" to={`/chantiers/${etat.projet_exemple_id}`}>l'ouvrir</Link></>}</p>
          ) : (
            <Button type="button" variant="outline" className="rounded-none self-start" onClick={projetExemple} disabled={envoi} data-testid="premier-jour-projet-exemple">
              Créer le projet exemple
            </Button>
          )}
        </div>
      </div>
      <div className="flex justify-end">
        <Link to="/dashboard" className="text-sm underline text-[#1E3A5F]">Aller au tableau de bord</Link>
      </div>
    </div>
  );
}
